import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  AlertCircle,
  Square,
  CheckSquare,
  List,
  Package,
  ArrowRight,
  CheckCircle2,
  XCircle,
  PlusCircle,
} from "lucide-react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  checkWarehouseAvailability,
  assignWarehouse,
  fetchPendingDispatch,
  clearStockAvailability,
} from "../../../store/slices/orderSlice";
import { fetchProductsList } from "../../../store/slices/productSlice";
import { fetchRawMaterialsThunk } from "../../../store/slices/warehouseSlice";
import toast from "react-hot-toast";
import { Edit3 } from "lucide-react";
import { usePreventWheelChange } from "../../../utils/usePreventWheelChange";
import AddItemsComponent from "../../BillOfMaterials/AddItemsToBOM";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  warehouseOptions: { value: string; label: string }[];
  fetchPendingRequests?: () => void;
}

const WarehouseAssignModal: React.FC<Props> = ({
  isOpen,
  onClose,
  order,
  warehouseOptions,
  fetchPendingRequests,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const { loading, stockAvailability, isCheckingStock } = useSelector(
    (state: RootState) => state.order,
  );

  const products = useSelector(
    (state: RootState) => state.product.products || [],
  );
  const rawMaterials = useSelector(
    (state: RootState) => state.warehouse.rawMaterials || [],
  );
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [qty, setQty] = useState(0);
  const [editSelectedProduct, setEditSelectedProduct] = useState<any>(null);
  const [editSelectedRawMaterial, setEditSelectedRawMaterial] =
    useState<any>(null);
  const [editLoading, setEditLoading] = useState(false);

  const [selectedItemIds, setSelectedItemIds] = useState<
    Record<string, boolean>
  >({});

  const [adjustments, setAdjustments] = useState<
    Record<string, { qty: number; unit: string }>
  >({});

  const [transportPrice, setTransportPrice] = useState<string>("");

  const [updatedOrder, setUpdatedOrder] = useState<any>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const rowRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleScrollWheelNumberInput = usePreventWheelChange();

  const loadProducts = useCallback(async () => {
    await dispatch(fetchProductsList());
  }, [dispatch]);

  useEffect(() => {
    if (products.length <= 0) {
      loadProducts();
    }
  }, [loadProducts, products.length]);

  const loadRawMaterialOptions = useCallback(
    async (inputValue: string) => {
      try {
        const result = await dispatch(
          fetchRawMaterialsThunk({
            page: 1,
            limit: 20,
            search: inputValue,
            from: "warehouse",
          }),
        ).unwrap();

        return (result.data || []).map((material: any) => ({
          value: material._id,
          label: material.name,
          unit: material.unit,
        }));
      } catch (error) {
        return [];
      }
    },
    [dispatch],
  );

  const openEditModal = useCallback(
    (item: any, index: number) => {
      setEditingItem({ ...item, index });
      setQty(item.quantity);
      setSelectedIndex(index);
      if (item.product) {
        const productId = item.product?._id || String(item.product);
        const selectedProduct = products.find(
          (product: any) => product._id === productId,
        );

        setEditSelectedProduct(
          selectedProduct
            ? { value: selectedProduct._id, label: selectedProduct.productName }
            : null,
        );
        setEditSelectedRawMaterial(null);
      } else {
        const materialId =
          item.rawMaterial?._id || String(item.rawMaterial || "");
        const selectedMaterial = rawMaterials.find(
          (material: any) => material._id === materialId,
        );

        setEditSelectedRawMaterial(
          selectedMaterial
            ? {
                value: selectedMaterial._id,
                label: selectedMaterial.name,
                unit: selectedMaterial.unit,
              }
            : null,
        );
        setEditSelectedProduct(null);
      }

      setIsEditModalOpen(true);
    },
    [products, rawMaterials],
  );
  const [editedGoods, setEditedGoods] = useState<any[]>([]);

  const editSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      padding: "0.2rem",
      fontSize: "0.875rem",
      borderRadius: "0.5rem",
      borderColor: "#CBD5E0",
      minHeight: "2.5rem",
      "&:hover": { borderColor: "#CBD5E0" },
      "&:focus": {
        borderColor: "#3B82F6",
        boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.5)",
      },
    }),
    menu: (provided: any) => ({ ...provided, zIndex: 99999 }),
    menuPortal: (provided: any) => ({ ...provided, zIndex: 99999 }),
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedWarehouse(null);
      setAdjustments({});
      setSelectedItemIds({});
      setUpdatedOrder(null);
      dispatch(clearStockAvailability());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && order?.warehouse) {
      const warehouseId =
        typeof order.warehouse === "object"
          ? order.warehouse._id
          : order.warehouse;

      const warehouseLabel =
        typeof order.warehouse === "object"
          ? order.warehouse.name
          : "Assigned Warehouse";

      const option = warehouseOptions.find(
        (opt) => opt.value === warehouseId,
      ) || {
        value: warehouseId,
        label: warehouseLabel,
      };

      setSelectedWarehouse(option);

      dispatch(
        checkWarehouseAvailability({
          orderId: order._id,
          warehouseId: option.value,
          goods: editedGoods,
        }),
      );
    }
  }, [isOpen, order, warehouseOptions, dispatch, editedGoods]);

  const processedGoods = useMemo(() => {
    const goodsArray = updatedOrder?.goods || order?.goods;
    if (!goodsArray) return [];

    return goodsArray.map((item: any, index: number) => {
      const id = String(
        item.product?._id ||
          item.rawMaterial?._id ||
          item.product ||
          item.rawMaterial,
      );

      const dispatched = Number(item.dispatchedQty) || 0;
      const total = Number(item.quantity) || 0;
      const remaining = Math.max(0, total - dispatched);
      const done = remaining < 0.0001;

      return {
        ...item,
        uniqueId: id,
        rowKey: `${id}-${index}`,
        dispatched,
        total,
        remaining,
        isCompleted: done,
      };
    });
  }, [updatedOrder, order]);

  const stockMap = useMemo(() => {
    const map: Record<string, any> = {};

    stockAvailability?.items?.forEach((s: any) => {
      map[s.itemId] = s;
    });

    return map;
  }, [stockAvailability]);

  useEffect(() => {
    if (isOpen && processedGoods.length > 0) {
      const initialAdj: Record<string, any> = {};
      const initialSelected: Record<string, boolean> = {};

      processedGoods.forEach(
        (item: {
          rowKey: string;
          isCompleted: any;
          remaining: any;
          unit: any;
        }) => {
          initialAdj[item.rowKey] = {
            qty: item.isCompleted ? 0 : item.remaining,
            unit: item.unit || "piece",
          };

          initialSelected[item.rowKey] = !item.isCompleted;
        },
      );

      setAdjustments(initialAdj);
      setSelectedItemIds(initialSelected);
    }
  }, [isOpen, processedGoods]);

  const validation = useMemo(() => {
    if (!stockAvailability?.items) return { isError: false };

    const issues = processedGoods.filter((item: any) => {
      const stock = stockMap[item.uniqueId];
      return (
        selectedItemIds[item.rowKey] &&
        (Number(adjustments[item.rowKey]?.qty) || 0) >
          (stock?.availableQty || 0)
      );
    });

    return {
      isError: issues.length > 0,
    };
  }, [
    processedGoods,
    stockAvailability,
    stockMap,
    selectedItemIds,
    adjustments,
  ]);

  const handleAdjustmentChange = useCallback(
    (id: string, field: "qty" | "unit", value: any) => {
      setAdjustments((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value,
        },
      }));
    },
    [],
  );

  const scrollToRow = useCallback((id: string) => {
    rowRefs.current[id]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  const handleConfirm = async () => {
    if (
      !order ||
      !selectedWarehouse ||
      validation.isError ||
      isCheckingStock ||
      transportPrice === ""
    )
      return;

    const finalAdjustments = processedGoods
      .filter((item: { rowKey: string }) => selectedItemIds[item.rowKey])
      .map(
        (item: { uniqueId: string | number; rowKey: string; type: any }) => ({
          itemId: item.uniqueId,
          quantity: Number(adjustments[item.rowKey].qty),
          unit: adjustments[item.rowKey].unit,
          type: item.type,
        }),
      );

    if (finalAdjustments.length === 0)
      return toast.error("Select at least one item.");

    await dispatch(
      assignWarehouse({
        orderId: order._id,
        warehouseId: selectedWarehouse.value,
        adjustments: finalAdjustments,
        transportPrice,
        goods: editedGoods,
      }),
    );

    dispatch(fetchPendingDispatch({ page: 1 }));
    setEditedGoods([]);
    onClose();
  };
  const handleSaveEdit = useCallback(() => {
    if (!editingItem) return;

    if (editingItem.product && !editSelectedProduct) {
      toast.error("Please select a product.");
      return;
    }

    if (!editingItem.product && !editSelectedRawMaterial) {
      toast.error("Please select a raw material.");
      return;
    }

    const currentGoods = updatedOrder?.goods || order?.goods || [];
    const updatedGoods = [...currentGoods];

    const oldItem = currentGoods[editingItem.index];

    const oldKey = editingItem.product
      ? String(oldItem.product?._id || oldItem.product)
      : String(oldItem.rawMaterial?._id || oldItem.rawMaterial);

    const newKey = editingItem.product
      ? editSelectedProduct.value
      : editSelectedRawMaterial.value;

    const type = editingItem.product ? "product" : "material";

    const updatedItem = {
      ...oldItem,
      quantity: qty,
      product: editingItem.product
        ? {
            ...oldItem.product,
            _id: editSelectedProduct.value,
            productName: editSelectedProduct.label,
          }
        : undefined,
      rawMaterial: !editingItem.product
        ? {
            ...oldItem.rawMaterial,
            _id: editSelectedRawMaterial.value,
            name: editSelectedRawMaterial.label,
          }
        : undefined,
    };

    updatedGoods[editingItem.index] = updatedItem;

    setUpdatedOrder({
      ...(updatedOrder || order),
      goods: updatedGoods,
    });

    setEditedGoods((prev) => {
      const mergedGoods = [...prev];

      // Helper to safely upsert items into the payload array
      const upsertItemPayload = (payload: any) => {
        const idx = mergedGoods.findIndex((g) =>
          g.type === payload.type
            ? payload.type === "product"
              ? String(g.product) === String(payload.product)
              : String(g.rawMaterial) === String(payload.rawMaterial)
            : false,
        );

        if (idx !== -1) {
          mergedGoods[idx] = payload;
        } else {
          mergedGoods.push(payload);
        }
      };

      // If the product/material ID was changed to a NEW item:
      if (oldKey !== newKey) {
        // 1. Send the OLD item with quantity 0 so the backend deletes it
        upsertItemPayload({
          type,
          product: editingItem.product ? oldKey : undefined,
          rawMaterial: !editingItem.product ? oldKey : undefined,
          quantity: 0,
        });

        // 2. Send the NEW item with the actual quantity
        upsertItemPayload({
          type,
          product: editingItem.product ? newKey : undefined,
          rawMaterial: !editingItem.product ? newKey : undefined,
          quantity: qty,
          price: updatedItem.price || 0,
          gst: updatedItem.gst || 0,
          hsn: updatedItem.hsn || 0,
          discount: updatedItem.discount || 0,
          isExtra: updatedItem.isExtra || false,
        });
      } else {
        // ID didn't change (only quantity changed)
        upsertItemPayload({
          type,
          product: editingItem.product ? newKey : undefined,
          rawMaterial: !editingItem.product ? newKey : undefined,
          quantity: qty,
          price: updatedItem.price || 0,
          gst: updatedItem.gst || 0,
          hsn: updatedItem.hsn || 0,
          discount: updatedItem.discount || 0,
          isExtra: updatedItem.isExtra || false,
        });
      }

      if (selectedWarehouse != null) {
        dispatch(
          checkWarehouseAvailability({
            orderId: order._id,
            warehouseId: selectedWarehouse.value,
            goods: mergedGoods,
          }),
        );
      }

      return mergedGoods;
    });

    setIsEditModalOpen(false);
    setEditingItem(null);
    setQty(0);
  }, [
    editingItem,
    editSelectedProduct,
    editSelectedRawMaterial,
    updatedOrder,
    order,
    qty,
    selectedWarehouse,
    dispatch,
  ]);

  const addNewItems = (items: any[]) => {
    setShowAddItemsModal(false);

    if (Array.isArray(items) && items.length > 0) {
      const baseOrder = updatedOrder || order;
      const nextGoods = [...(baseOrder?.goods || [])];
      const editedGoodsUpdate: any[] = [];
      items.forEach((item: any) => {
        const isProduct = item.type === "product";
        const itemId = isProduct ? item.product : item.rawMaterial;
        const existingIndex = nextGoods.findIndex((goodsItem: any) => {
          const currentId = isProduct
            ? goodsItem.product?._id || goodsItem.product
            : goodsItem.rawMaterial?._id || goodsItem.rawMaterial;
          return currentId === itemId;
        });
        let quantity = item.quantity;
        if (existingIndex >= 0) {
          quantity =
            Number(nextGoods[existingIndex].quantity || 0) +
            Number(item.quantity || 0);

          nextGoods[existingIndex] = {
            ...nextGoods[existingIndex],
            quantity,
          };
        } else {
          nextGoods.push({
            ...item,
            product: isProduct ? item.product : undefined,
            rawMaterial: isProduct ? undefined : item.rawMaterial,
          });
        }

        editedGoodsUpdate.push({
          ...item,
          quantity,
          product: isProduct ? itemId : null,
          rawMaterial: isProduct ? null : itemId,
        });
      });

      setUpdatedOrder({
        ...(baseOrder || order),
        goods: nextGoods,
      });

      setEditedGoods((prev) => {
        const mergedGoods = [...prev];
        editedGoodsUpdate.forEach((item: any) => {
          const isProduct = item.type === "product";
          const itemId = isProduct ? item.product : item.rawMaterial;
          const existingIndex = mergedGoods.findIndex((goodsItem: any) => {
            const currentId = isProduct
              ? goodsItem.product
              : goodsItem.rawMaterial;
            return currentId === itemId;
          });

          if (existingIndex >= 0) {
            let quantity =
              Number(mergedGoods[existingIndex].quantity || 0) +
              Number(item.quantity || 0);
            mergedGoods[existingIndex] = {
              ...mergedGoods[existingIndex],
              quantity,
            };
          } else {
            mergedGoods.push({
              ...item,
              product: isProduct ? item.product : undefined,
              rawMaterial: isProduct ? undefined : item.rawMaterial,
            });
          }
        });
        if (selectedWarehouse != null && selectedWarehouse != undefined) {
          dispatch(
            checkWarehouseAvailability({
              orderId: order._id,
              warehouseId: selectedWarehouse.value,
              goods: mergedGoods,
            }),
          );
        }
        return mergedGoods;
      });
    }
  };
  const isButtonDisabled =
    !selectedWarehouse ||
    loading ||
    isCheckingStock ||
    validation.isError ||
    !stockAvailability ||
    transportPrice === "";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="transform-gpu bg-white rounded-[2rem] w-full max-w-[1300px] h-[92vh] shadow-2xl relative flex flex-col overflow-hidden"
          >
            <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white">
              <div className="flex items-center gap-4">
                <div className="bg-blue-600 p-2.5 rounded-xl text-white shadow-lg">
                  <Package size={22} />
                </div>

                <div>
                  <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">
                    Assign Warehouse & Qty
                  </h2>

                  <p className="text-xs text-slate-400 font-bold">
                    {order.orderNumber} |{" "}
                    {order.customerId?.name ||
                      order.whatsappCustomerId?.productRegistration?.name ||
                      "Customer"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="w-64">
                  <Select
                    options={warehouseOptions}
                    value={selectedWarehouse}
                    isDisabled={!!order.warehouse}
                    onChange={(s) => {
                      setSelectedWarehouse(s);

                      if (s) {
                        requestAnimationFrame(() => {
                          dispatch(
                            checkWarehouseAvailability({
                              orderId: order._id,
                              warehouseId: s.value,
                              goods: editedGoods,
                            }),
                          );
                        });
                      }
                    }}
                    placeholder="Select Warehouse..."
                    className="text-sm font-bold"
                  />
                </div>

                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-slate-400" />
                </button>
              </div>
            </div>
            <div className="flex justify-center py-4">
              <button
                type="button"
                onClick={() => setShowAddItemsModal(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
              >
                <PlusCircle size={18} className="shrink-0" />
                <span>Add Items</span>
              </button>
            </div>
            <div className="flex-1 min-h-0 flex overflow-hidden">
              {/* LEFT */}
              <div className="flex-1 overflow-y-auto will-change-transform p-8 custom-scrollbar">
                <div className="grid grid-cols-12 gap-4 mb-4 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                  <div className="col-span-1">INC.</div>

                  <div className="col-span-4 text-left">
                    Product / Material Details
                  </div>

                  <div className="col-span-3">Dispatched / Total</div>

                  <div className="col-span-4">Assignment Adjustment</div>
                </div>

                <div className="space-y-3">
                  {processedGoods.map((item: any, index: number) => {
                    const isTicked = selectedItemIds[item.rowKey];

                    const stock = stockMap[item.uniqueId];

                    const currentAdjQty =
                      Number(adjustments[item.rowKey]?.qty) || 0;

                    const isInsufficient =
                      isTicked && stock && currentAdjQty > stock.availableQty;

                    return (
                      <div
                        key={item.rowKey}
                        ref={(el) => (rowRefs.current[item.rowKey] = el)}
                        className={`grid grid-cols-12 gap-4 items-center p-4 rounded-2xl border transition-all ${
                          item.isCompleted
                            ? "opacity-60 bg-emerald-50/30 border-emerald-100"
                            : !isTicked
                              ? "opacity-40 grayscale bg-slate-50 border-slate-100"
                              : isInsufficient
                                ? "bg-red-50 border-red-200"
                                : "bg-white border-slate-100 shadow-sm"
                        }`}
                      >
                        <div className="col-span-1 flex justify-center">
                          {item.isCompleted ? (
                            <CheckCircle2
                              size={22}
                              className="text-emerald-500"
                            />
                          ) : (
                            <button
                              onClick={() =>
                                setSelectedItemIds((prev) => ({
                                  ...prev,
                                  [item.rowKey]: !prev[item.rowKey],
                                }))
                              }
                              className={`p-2 rounded-xl transition-all ${
                                isTicked
                                  ? "text-blue-600 bg-blue-50 shadow-sm"
                                  : "text-slate-300"
                              }`}
                            >
                              {isTicked ? (
                                <CheckSquare size={22} />
                              ) : (
                                <Square size={22} />
                              )}
                            </button>
                          )}
                        </div>

                        <div className="col-span-4 flex items-center gap-3">
                          <div
                            className={`w-1 h-8 rounded-full ${
                              item.isCompleted
                                ? "bg-emerald-500"
                                : isTicked
                                  ? isInsufficient
                                    ? "bg-red-500"
                                    : "bg-blue-500"
                                  : "bg-slate-300"
                            }`}
                          />

                          <div className="truncate">
                            <div className="truncate flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEditModal(item, index)}
                                className="flex items-center gap-2 text-sm font-bold text-slate-800 truncate hover:text-blue-600"
                              >
                                <span className="truncate">
                                  {item.product?.productName ||
                                    item.rawMaterial?.name ||
                                    item.name}
                                </span>
                                <Edit3 size={14} className="text-slate-400" />
                              </button>

                              {item.isExtra === true && (
                                <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 text-[9px] font-black uppercase shrink-0">
                                  Extra
                                </span>
                              )}
                            </div>

                            <p className="text-[9px] font-black text-slate-400 uppercase">
                              {item.type}
                            </p>
                          </div>
                        </div>

                        <div className="col-span-3 text-center">
                          <div className="inline-flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                            <span className="text-xs font-bold text-slate-400">
                              {item.dispatched}
                            </span>

                            <ArrowRight size={10} className="text-slate-300" />

                            <span className="text-xs font-bold text-slate-800">
                              {item.total}
                            </span>

                            <span className="text-[9px] font-bold text-slate-400 uppercase">
                              {item.unit}
                            </span>
                          </div>
                        </div>

                        <div className="col-span-4 flex items-center gap-2 bg-slate-50 p-1 rounded-xl border border-slate-200">
                          <input
                            type="number"
                            disabled={!isTicked || item.isCompleted}
                            value={adjustments[item.rowKey]?.qty || 0}
                            onChange={(e) =>
                              handleAdjustmentChange(
                                item.rowKey,
                                "qty",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            className="w-20 p-2 bg-white border border-slate-100 rounded-lg text-sm font-bold text-center outline-none"
                          />

                          <div className="flex-1 text-[10px] font-bold text-slate-400 uppercase text-center">
                            {item.unit}
                          </div>

                          {stock && isTicked && (
                            <div
                              className={`px-2 py-1 rounded text-[9px] font-bold ${
                                isInsufficient
                                  ? "bg-red-100 text-red-600"
                                  : "bg-emerald-100 text-emerald-600"
                              }`}
                            >
                              Stock: {stock.availableQty}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT */}
              <div className="w-[340px] border-l border-slate-100 bg-slate-50/50 flex flex-col p-6">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-6">
                  <List size={14} /> Selection Guide
                </h3>

                <div className="flex-1 overflow-y-auto will-change-transform space-y-2 pr-2 custom-scrollbar">
                  {processedGoods.map((item: any) => {
                    const isTicked = selectedItemIds[item.rowKey];

                    const adjQty = Number(adjustments[item.rowKey]?.qty) || 0;

                    return (
                      <div
                        key={item.rowKey}
                        onClick={() => scrollToRow(item.rowKey)}
                        className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                          isTicked
                            ? "bg-white border-blue-200 shadow-sm"
                            : "bg-slate-100 border-transparent opacity-60"
                        }`}
                      >
                        <div className="flex items-center gap-3 truncate">
                          <div
                            className={`w-2 h-2 rounded-full shrink-0 ${
                              item.isCompleted
                                ? "bg-emerald-500"
                                : isTicked
                                  ? "bg-blue-500"
                                  : "bg-slate-300"
                            }`}
                          />

                          <span
                            className={`text-[11px] font-bold truncate ${
                              isTicked
                                ? "text-slate-700"
                                : "text-slate-400 line-through"
                            }`}
                          >
                            {item.product?.productName ||
                              item.rawMaterial?.name ||
                              item.name}
                          </span>
                        </div>

                        {isTicked && (
                          <span className="text-xs font-bold text-blue-600">
                            +{adjQty}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 space-y-3">
                  {validation.isError && (
                    <div className="p-3 bg-red-50 text-red-600 rounded-xl border border-red-100 flex items-center gap-2 text-[10px] font-bold uppercase">
                      <AlertCircle size={14} /> Check red items
                    </div>
                  )}
                  <div className="bg-white border border-slate-200 rounded-2xl p-3">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                      Transport Price
                    </label>

                    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3">
                      <span className="text-sm font-bold text-slate-500 mr-2">
                        ₹
                      </span>

                      <input
                        type="number"
                        value={transportPrice}
                        onChange={(e) => {
                          setTransportPrice(e.target.value);
                        }}
                        placeholder="Enter amount"
                        className="w-full py-3 bg-transparent outline-none text-sm font-bold text-slate-800"
                      />
                    </div>
                    <label className="block text-[8px] pt-2 font-bold text-slate-400 uppercase tracking-widest mb-2">
                      NOTE: Transport price increments for partial orders.
                    </label>
                  </div>
                  <button
                    onClick={handleConfirm}
                    disabled={isButtonDisabled}
                    className="w-full py-4 bg-slate-900 text-white font-bold rounded-2xl shadow-xl disabled:opacity-20 transition-all uppercase text-xs tracking-widest active:scale-95 flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    ) : (
                      "Confirm Assignment"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {isEditModalOpen && (
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Edit{" "}
                      {editingItem?.product?._id ? "Product" : "Raw Material"}
                    </h3>

                    <p className="mt-1 text-sm text-slate-500">
                      Select a new value and quantity.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingItem(null);
                      setQty(0);
                    }}
                    className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Select */}
                <div className="mt-5">
                  <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                    {editingItem?.product?._id
                      ? "Select Product"
                      : "Select Raw Material"}
                  </label>

                  {editingItem?.product?._id ? (
                    <Select
                      options={products.map((product: any) => ({
                        value: product._id,
                        label: product.productName,
                      }))}
                      value={editSelectedProduct}
                      onChange={(value) => setEditSelectedProduct(value)}
                      placeholder="Select product..."
                      styles={editSelectStyles}
                      className="text-sm"
                    />
                  ) : (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadRawMaterialOptions}
                      value={editSelectedRawMaterial}
                      onChange={(value) => setEditSelectedRawMaterial(value)}
                      placeholder="Select raw material..."
                      styles={editSelectStyles}
                      menuPortalTarget={document.body}
                      isClearable
                    />
                  )}
                </div>

                {/* Quantity */}
                <div className="mt-5">
                  <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                    Quantity
                  </label>

                  <div className="flex items-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                    <button
                      type="button"
                      onClick={() =>
                        setQty((prev) => Math.max(1, Number(prev) - 1))
                      }
                      className="flex h-12 w-12 items-center justify-center text-lg font-bold text-slate-700 transition hover:bg-slate-100"
                    >
                      -
                    </button>

                    <input
                      type="number"
                      min={1}
                      onWheel={handleScrollWheelNumberInput}
                      value={qty}
                      onChange={(e) => {
                        setQty(Number(e.target.value));
                      }}
                      className="h-12 flex-1 border-x border-slate-200 bg-white text-center text-sm font-bold text-slate-900 outline-none"
                    />

                    <button
                      type="button"
                      onClick={() => setQty((prev) => Number(prev) + 1)}
                      className="flex h-12 w-12 items-center justify-center text-lg font-bold text-slate-700 transition hover:bg-slate-100"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingItem(null);
                      setQty(0);
                    }}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    disabled={editLoading}
                    className="flex min-w-[110px] items-center justify-center rounded-2xl bg-blue-700 shadow-sm px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-800 disabled:opacity-50"
                  >
                    {editLoading ? (
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
          {showAddItemsModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="text-lg font-bold text-gray-900">
                    Add Items to {order.orderNumber}
                  </h3>
                  <button
                    onClick={() => setShowAddItemsModal(false)}
                    className="p-2 hover:bg-white rounded-full text-gray-400 hover:text-gray-900 transition-all"
                  >
                    <XCircle size={20} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <AddItemsComponent checkBom={false} onSuccess={addNewItems} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </AnimatePresence>
  );
};

export default WarehouseAssignModal;
